import { CreateAttempt } from '@/common/types/session';
import { playMakeTheWordAudio, playWordAudio } from '@/common/utils/audio';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getMorphemeById,
  getMorphemesByIds,
} from '@/common/utils/scopeAndSequence';
import {
  segmentTypedAnswer,
  validateSegmentedAnswer,
} from '@/common/utils/segmentTypedAnswer';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getSession,
  getStudent,
  getWordSums,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { WordSumsCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import { WordSumsState } from '@/game/GameComponents/PhaserGame/Types/IWordSumsState';
import {
  CardFeedback,
  KeyboardFeedback,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { calculateProgress, shuffleItems } from '@/game/gameHelpers';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { getDistractorMorphemes } from '@/game/WordSums/wordSumsHelpers';
import { v4 as uuidv4 } from 'uuid';

export class WordSumsController extends MinigameController {
  override getMaxBoardCards(): number {
    return getWordSums().wordToBuild?.morphemes.length || 0;
  }

  override startRound(): void {
    const isRoundEnded = getGeneralGameState().isRoundEnded;
    if (isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }
    this.handleWordSumsDeal();
  }

  async handleWordSumsDeal() {
    const wordSumsState: WordSumsState = getWordSums();
    const currentWordIndex = wordSumsState.usedWords.length;
    const generalGameState = getGeneralGameState();
    const level = getStudent().level;
    const words = generalGameState.gameData.wordSums || [];
    const currentWord = words[currentWordIndex];

    GameInstance.instance.updateWordSums({
      wordToBuild: currentWord,
    });

    const wordMorphemes = getMorphemesByIds(currentWord.morphemes);
    const distractorCardsEnabled = getStudent().settings.distractorCardsEnabled;
    const distractorMorphemes = getDistractorMorphemes(
      wordMorphemes,
      level,
      distractorCardsEnabled,
    );
    const allMorphemes = [...wordMorphemes, ...distractorMorphemes];
    const shuffledMorphemes = shuffleItems(allMorphemes);
    const dealtMorphemeCards: WordSumsCard[] = shuffledMorphemes.map(
      (morpheme) => ({
        id: uuidv4(),
        morpheme,
      }),
    );

    GameInstance.instance.updateWordSums({
      dealtMorphemes: dealtMorphemeCards,
    });
  }

  override async playAudioForRound() {
    try {
      // Play audio for the word
      if (getWordSums().usedWords.length === 0) {
        await playMakeTheWordAudio();
      }
      await playWordAudio(getWordSums().wordToBuild?.spelling || '');
    } catch (error) {
      console.error('Error during audio playback:', error);
    }
  }

  override async checkMinigameAnswer() {
    const wordSumsState = getWordSums();
    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }

    const studentAttempt = this.minigame.getInput().trim().toLowerCase();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const wordToBuild = wordSumsState.wordToBuild;
    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }
    // Add attempt
    const currentAttemptStartedAt = getSession().currentAttemptStartedAt;
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'wordsums',
      correctAnswer: wordToBuild.spelling,
      correctAnswerId: wordToBuild.id,
      studentAnswer: studentAttempt,
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    const answerWasCorrect = studentAttempt === wordToBuild.spelling;

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(answerWasCorrect);

    if (answerWasCorrect) {
      // Word is correct

      // Add word to used words
      GameInstance.instance.updateWordSums({
        usedWords: [...wordSumsState.usedWords, wordToBuild],
      });
    }

    // Calculate and update progress
    const newProgress = calculateProgress(
      getGeneralGameState(),
      getWordSums(),
      100,
    );
    GameInstance.instance.updateGeneralGameState({
      progress: newProgress,
    });

    if (answerWasCorrect) {
      // Play mastered sfx.
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: true,
        forceIndividualFeedback: true,
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      // Play incorrect sfx
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        partiallyCorrectAnswer:
          this.getIsPartiallyCorrectAnswer(studentAttempt),
        forceIndividualFeedback: true,
        forceReadout: true,
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  override createCardList(minigameScene: Minigame): {
    dealtCards: (StudentGameCard | null)[];
    boardCards: (StudentGameCard | null)[];
  } {
    const wordSums = getWordSums();
    return {
      dealtCards: wordSums.dealtMorphemes.map((morpheme) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(
          newCard,
          { id: uuidv4(), morpheme: morpheme.morpheme },
          'hand',
        );
        return newCard;
      }),
      boardCards: [],
    };
  }

  override async playInstructions(): Promise<void> {
    const wordToBuild = getWordSums().wordToBuild;
    const student = getStudent();
    const studentId = student.id;

    if (studentId && wordToBuild) {
      trackMixpanelEvent('Replay word button clicked', {
        word: wordToBuild.spelling,
      });
    }

    await playWordAudio(getWordSums().wordToBuild?.spelling || '');
  }

  override getCardFeedback(): CardFeedback[] {
    const wordSums = getWordSums();
    const boardState = this.minigame.getBoardDropSite().layoutGroup!;
    const cards = boardState.getMinigameCards() as StudentGameCard[];
    return cards.map((card, i) => {
      const morphemeId = wordSums.wordToBuild?.morphemes[i];
      const morpheme = getMorphemeById(morphemeId!);
      return {
        card,
        isCorrect: card.cardData?.morpheme?.spelling === morpheme?.spelling,
      };
    });
  }

  override getKeyboardFeedback(): KeyboardFeedback[] {
    const studentAttempt = this.minigame.getTypedInput();
    if (!studentAttempt) return [];

    // First highlight and read out the grapheme.
    // If the student wrote an actual word, get the graphemes from that.
    // Otherwise, infer the graphemes from the letters.
    const word = getWordSums().wordToBuild!;
    const morphemes = getMorphemesByIds(word.morphemes);

    const morphemeSpellings = morphemes.map((morpheme) => morpheme.spelling);
    const studentAnswer = segmentTypedAnswer(studentAttempt, morphemeSpellings);
    const validation = validateSegmentedAnswer(
      studentAnswer,
      morphemeSpellings,
    );

    let letterIndex = 0;
    return validation.map((validation, index) => {
      const startIndex = letterIndex;
      const answerSegment = studentAnswer[index] ?? '';

      letterIndex += answerSegment.length;
      const isCorrect: boolean = validation;
      let morpheme = undefined;
      if (isCorrect) {
        morpheme = morphemes.find(
          (morpheme) => morpheme.spelling === answerSegment,
        );
      }
      return {
        isCorrect,
        letters: this.minigame
          .getPlayerTypingInputs()[0]
          .getCharacterArray()
          .slice(startIndex, letterIndex) as SingleCharacterText[],
        morpheme: morpheme,
      };
    });
  }

  override async readFinalAnswer() {
    const wordToBuild = getWordSums().wordToBuild;
    if (!wordToBuild) return;
    await playWordAudio(wordToBuild.spelling);
  }

  override canShowArticulatoryGestures(): boolean {
    return false;
  }

  override canShowWordSegmentation(): boolean {
    return getStudent().settings?.wordSegmentationEnabled ?? false;
  }

  getIsPartiallyCorrectAnswer(studentAttempt: string) {
    const wordToBuild = getWordSums().wordToBuild;
    if (!wordToBuild) {
      console.error('No word to build found');
      return false;
    }
    const attemptInWord = wordToBuild.spelling
      .toLowerCase()
      .includes(studentAttempt.toLowerCase());
    const attemptAtStart = wordToBuild.spelling.indexOf(studentAttempt) === 0;
    return attemptInWord && attemptAtStart;
  }
}
